import { BadgeCheckIcon } from "@heroicons/react/solid";
import { compact, isEmpty, orderBy } from "lodash";
import { stringifyUrl } from "query-string";
import Tooltip from "rc-tooltip";
import React, { useCallback, useMemo } from "react";
import { Link, RouteComponentProps, useLocation } from "react-router-dom";
import InfoIcon from "../../../../components/icons/InfoIcon";
import DisplayLabels from "../../../../components/labels/DisplayLabels";
import StatusDot from "../../../../components/StatusDot";
import { useHotspots } from "../../../../context/hotspots";
import { useLineOfSight } from "../../../../context/lineOfSight";
import { useLocations } from "../../../../context/locations";
import { avatarsApi } from "../../../../utils/constants";
import {
  ClassificationValue,
  formatHotspotName
} from "../../../../utils/hotspotUtils";
import { PhpHotspot } from "../../../../utils/types";
import useMapConfig from "../../../../utils/useMapConfig";
import TablePagination from "../../../Workspace/common/TablePagination";
import usePagination, {
  PaginationKey
} from "../../../Workspace/common/usePagination";
import { DEFAULT_SIMULATION_HEIGHT } from "./SuperSimulationSidebar";

interface Props
  extends RouteComponentProps<{
    id: string;
    simulationUuid: string;
    section: string;
  }> {}

const VisibleHotspotsTab: React.FC<Props> = (props) => {
  const renderDistance = useMapConfig((state) => state.renderDistance);
  const {
    currentLocation,
    locations,
    currentSuperSimulation,
    setSelectPointsOnMapConfig
  } = useLocations();
  const { hotspotsMap, walletVerificationStatusMap } = useHotspots();
  const {
    clearPoints,
    setStartPoint,
    setEndPoint,
    setStartHeight,
    setEndHeight
  } = useLineOfSight();

  const location = useMemo(
    () =>
      currentLocation ||
      (props.match.params.id ? locations[props.match.params.id] : undefined),
    [currentLocation, locations, props.match.params.id]
  );

  const visibleHotspots = useMemo(
    () => currentSuperSimulation?.simulation_report?.vh || [],
    [currentSuperSimulation?.simulation_report?.vh]
  );

  const { search: queryParamsString } = useLocation();

  const hotspotsToRender = useMemo(
    () =>
      compact(visibleHotspots.map((hotspotId) => hotspotsMap[hotspotId])).map(
        (hotspot) => ({
          hotspot,
          transmitScale: hotspot.reward_scale || 0,
          witnessRewardUnits: hotspot.rx_reward_unit,
          witnessScore: hotspot.witness_score || 0,
          height: hotspot.elevation || DEFAULT_SIMULATION_HEIGHT
        })
      ),
    [hotspotsMap, visibleHotspots]
  );

  const { startIndex, endIndex } = usePagination(
    useCallback(
      (state) =>
        state.getPaginationInfo(
          PaginationKey.locationWitnessesVisibleHotspots,
          hotspotsToRender.length,
          queryParamsString
        ),
      [hotspotsToRender.length, queryParamsString]
    )
  );

  if (isEmpty(hotspotsToRender)) {
    return (
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-50">
        There are no visible hotspots from this location according to the
        simulation.
      </p>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-500">
              <thead className="bg-gray-50 text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    {hotspotsToRender.length.toLocaleString(undefined)} hotspot
                    {hotspotsToRender.length > 1 ? "s" : ""}
                  </th>

                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    style={{ minWidth: "11rem" }}
                  >
                    <div className="flex items-center">
                      Witness score
                      <Tooltip
                        placement="top"
                        overlay="The Witness Score is a number that represents the PoC rewards that hotspots can earn for witnessing this hotspot. It is calculated as the product of the transmit scale and witness reward units of this hotspot."
                      >
                        <InfoIcon className="inline-flex ml-1 h-4 w-4" />
                      </Tooltip>
                    </div>
                  </th>

                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Transmit scale
                  </th>

                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Witness reward units
                  </th>

                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Height
                  </th>

                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800">
                {orderBy(hotspotsToRender, ["witnessScore"], ["desc"])
                  .slice(startIndex, endIndex)
                  .map(
                    ({
                      hotspot,
                      transmitScale,
                      witnessRewardUnits,
                      witnessScore,
                      height
                    }) => (
                      <tr key={hotspot.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link
                            className="relative flex items-center space-x-3 group"
                            to={`/hotspots/${hotspot.id}/status`}
                          >
                            <div className="flex-shrink-0">
                              <span className="inline-block relative">
                                <img
                                  className="h-10 w-10 rounded-full"
                                  src={stringifyUrl({
                                    url: `${avatarsApi}/bottts/${hotspot.id}.svg`,
                                    query: { w: 40, h: 40 }
                                  })}
                                  alt="Hotspot avatar"
                                />
                                <StatusDot
                                  hotspot={hotspot}
                                  withoutTooltip
                                  className="absolute top-0 right-0 block"
                                />
                              </span>
                            </div>
                            <div className="flex-1 min-w-0 focus:outline-none">
                              <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline inline-flex items-center">
                                {((hotspot as PhpHotspot).verified_by ||
                                  !!walletVerificationStatusMap[
                                    hotspot.owner
                                  ]) && (
                                  <BadgeCheckIcon className="mr-1 h-5 w-5 text-green-400" />
                                )}
                                {formatHotspotName(hotspot)}
                              </p>
                              <DisplayLabels
                                linkedUuid={hotspot.id}
                                size="small"
                                contextWalletAddresses={[hotspot.owner]}
                              />
                            </div>
                          </Link>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <ClassificationValue
                            value={witnessScore}
                            withoutTransmitScaleTooltip
                          />
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <ClassificationValue value={transmitScale} />
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <ClassificationValue
                            value={witnessRewardUnits}
                            alternativeContent={witnessRewardUnits.toLocaleString(
                              undefined,
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }
                            )}
                            withoutTransmitScaleTooltip
                          />
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {renderDistance(height, false)}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {location && currentSuperSimulation?.height != null && (
                            <Link
                              to="/locations/line-of-sight"
                              onClick={() => {
                                clearPoints();
                                setSelectPointsOnMapConfig(undefined);
                                setStartPoint([location.lng, location.lat]);
                                setEndPoint([hotspot.lng, hotspot.lat]);
                                setStartHeight(
                                  Number(currentSuperSimulation.height)
                                );
                                setEndHeight(height);
                              }}
                              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400"
                            >
                              Open LOS
                            </Link>
                          )}
                        </td>
                      </tr>
                    )
                  )}
              </tbody>
            </table>
            <TablePagination
              paginationKey={PaginationKey.locationWitnessesVisibleHotspots}
              itemsCount={hotspotsToRender.length}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisibleHotspotsTab;
